import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Button } from './components/ui/button';
import { Badge } from './components/ui/badge';
import { Alert, AlertDescription } from './components/ui/alert';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { 
  Activity, DollarSign, Eye, TrendingUp, TrendingDown,
  LogOut, RefreshCw, BarChart, Users, Target,
  CheckCircle, XCircle, Globe, Clock, ChevronRight,
  AlertTriangle, Shield, MousePointer, Zap
} from 'lucide-react';
import CampaignDetail from './CampaignDetail';
import { getCurrencySymbol, formatCurrency } from './utils/currency';
import { getExchangeRates, convertCurrency, groupCampaignsByCurrency } from './utils/currencyExchange';

// const API_URL = 'http://localhost:8080';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

export default function Dashboard() {
  const [campaigns, setCampaigns] = useState([]);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [revenueStats, setRevenueStats] = useState(null);
  const [trafficQuality, setTrafficQuality] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  
  // New state for currency and date filtering
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [baseCurrency, setBaseCurrency] = useState('USD');
  const [exchangeRates, setExchangeRates] = useState(null);
  const [currencyGroups, setCurrencyGroups] = useState({});
  const [currencyBreakdown, setCurrencyBreakdown] = useState([]);

    // Add this new function
    const fetchCurrencyBreakdown = async () => {
    try {
        const token = localStorage.getItem('authToken');
        const dateParams = fromDate && toDate ? `?from=${fromDate}&to=${toDate}` : '';
        
        const response = await fetch(
        `${API_URL}/api/analytics/revenue-by-currency${dateParams}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
        );
        
        if (response.ok) {
        const data = await response.json();
        setCurrencyBreakdown(data.currency_breakdown || []);
        }
    } catch (error) {
        console.error('Failed to fetch currency breakdown:', error);
    }
    };
  const userType = localStorage.getItem('userType');
  const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
  const isPublisher = userType === 'publisher';

  useEffect(() => {
    // Set default date range (last 30 days)
    const today = new Date();
    const thirtyDaysAgo = new Date(today.getTime() - (30 * 24 * 60 * 60 * 1000));
    
    setToDate(today.toISOString().split('T')[0]);
    setFromDate(thirtyDaysAgo.toISOString().split('T')[0]);
    
    // Fetch exchange rates
    fetchExchangeRates();
    
    fetchAllData();
  }, []);

  // Fetch exchange rates when base currency changes
  useEffect(() => {
    fetchExchangeRates();
  }, [baseCurrency]);

  // Update currency grouping when campaigns change
  useEffect(() => {
    if (campaigns.length > 0) {
      const groups = groupCampaignsByCurrency(campaigns);
      setCurrencyGroups(groups);
    }
  }, [campaigns]);

  const fetchExchangeRates = async () => {
    try {
      const rates = await getExchangeRates(baseCurrency);
      setExchangeRates(rates);
    } catch (error) {
      console.error('Failed to fetch exchange rates:', error);
    }
  };

  const fetchAllData = async () => {
  setLoading(true);
  setError('');
  
  try {
    const token = localStorage.getItem('authToken');
    const headers = { 'Authorization': `Bearer ${token}` };
    const dateParams = fromDate && toDate ? `?from=${fromDate}&to=${toDate}` : '';

    // Dashboard stats
    const dashboardRes = await fetch(
      `${API_URL}${isPublisher ? '/api/publishers/analytics/dashboard' : '/api/analytics/dashboard'}${dateParams}`,
      { headers }
    );
    
    // Campaigns
    const campaignsRes = await fetch(
      `${API_URL}${isPublisher ? '/api/publishers/campaigns' : '/api/campaigns/'}`,
      { headers }
    );
    
    // Revenue
    const revenueRes = await fetch(
      `${API_URL}${isPublisher ? '/api/publishers/analytics/revenue' : '/api/analytics/revenue'}${dateParams || '?period=weekly'}`,
      { headers }
    );
    
    // Traffic quality
    const trafficRes = await fetch(
      `${API_URL}${isPublisher ? '/api/publishers/analytics/traffic-quality' : '/api/analytics/traffic-quality'}${dateParams || '?period=7'}`,
      { headers }
    );
    
    // Currency breakdown
    const currencyRes = await fetch(
      `${API_URL}${isPublisher ? '/api/publishers/analytics/revenue-by-currency' : '/api/analytics/revenue-by-currency'}${dateParams}`,
      { headers }
    );

    // Process each response independently
    if (dashboardRes.ok) {
      const dashboardData = await dashboardRes.json();
      setDashboardStats(dashboardData);
    }

    if (campaignsRes.ok) {
      const campaignsData = await campaignsRes.json();
      
      // NEW: Fetch conversion stats for each campaign
      const campaignsWithConversions = await Promise.all(
        campaignsData.map(async (campaign) => {
          try {
            const convRes = await fetch(
              `${API_URL}/api/campaigns/${campaign.id}/conversions/stats`,
              { headers }
            );
            const convStats = convRes.ok ? await convRes.json() : null;
            return {
              ...campaign,
              conversion_stats: convStats
            };
          } catch (err) {
            console.error(`Failed to fetch conversions for campaign ${campaign.id}`, err);
            return campaign;
          }
        })
      );
      
      setCampaigns(campaignsWithConversions);
    }

    if (revenueRes.ok) {
      const revenueData = await revenueRes.json();
      setRevenueStats(revenueData);
    }

    if (trafficRes.ok) {
      const trafficData = await trafficRes.json();
      setTrafficQuality(trafficData);
    }

    if (currencyRes.ok) {
      const currencyData = await currencyRes.json();
      console.log('Currency breakdown data:', currencyData);
      setCurrencyBreakdown(currencyData.currency_breakdown || []);
    } else {
      console.error('Currency API error:', currencyRes.status);
      setCurrencyBreakdown([]);
    }

  } catch (err) {
    setError('Failed to load data');
    console.error('Fetch error:', err);
  } finally {
    setLoading(false);
  }
};
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAllData();
    setRefreshing(false);
  };

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = '/';
  };

  // Calculate converted totals
  const getConvertedTotals = () => {
    let grandTotal = 0;
    const currencyTotals = {};

    Object.keys(currencyGroups).forEach(currency => {
      const group = currencyGroups[currency];
      currencyTotals[currency] = group.totalRevenue;
      
      if (exchangeRates) {
        const converted = convertCurrency(
          group.totalRevenue, 
          currency, 
          baseCurrency, 
          exchangeRates
        );
        grandTotal += converted;
      }
    });

    return { grandTotal, currencyTotals };
  };

  const { grandTotal, currencyTotals } = getConvertedTotals();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (selectedCampaign) {
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center space-x-4">
                <Activity className="h-8 w-8 text-blue-600" />
                <h1 className="text-xl font-bold">
                  {isPublisher ? 'Publisher Portal' : 'Campaign Tracker'}
                </h1>
                <Badge variant={isPublisher ? 'secondary' : 'default'}>
                  {isPublisher ? 'Publisher' : 'Company'}
                </Badge>
                {isPublisher && currentUser.company && (
                  <Badge variant="outline">{currentUser.company}</Badge>
                )}
              </div>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-600">
                  {currentUser.name || currentUser.username || currentUser.email}
                </span>
                <Button variant="ghost" size="sm" onClick={handleLogout}>
                  <LogOut className="h-4 w-4 mr-2" />
                  Logout
                </Button>
              </div>
            </div>
          </div>
        </header>
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <CampaignDetail 
            campaign={selectedCampaign} 
            onBack={() => setSelectedCampaign(null)} 
          />
        </main>
      </div>
    );
  }

  const clickTrend = revenueStats?.trend || 'stable';
  const qualityScore = trafficQuality?.quality_score || 0;
  const bestPerformingCampaign = campaigns.reduce((best, campaign) => 
    (!best || campaign.today_revenue > best.today_revenue) ? campaign : best
  , null);

  // Get predominant currency from campaigns (most used currency)
  const currencyCounts = campaigns.reduce((acc, campaign) => {
    const curr = campaign.currency || 'USD';
    acc[curr] = (acc[curr] || 0) + 1;
    return acc;
  }, {});
  const primaryCurrency = Object.keys(currencyCounts).reduce((a, b) => 
    currencyCounts[a] > currencyCounts[b] ? a : b, 'USD'
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <Activity className="h-8 w-8 text-blue-600" />
              <h1 className="text-xl font-bold">
                {isPublisher ? 'Publisher Portal' : 'Campaign Tracker'}
              </h1>
              <Badge variant={isPublisher ? 'secondary' : 'default'}>
                {isPublisher ? 'Publisher' : 'Company'}
              </Badge>
              {isPublisher && currentUser.company && (
                <Badge variant="outline">{currentUser.company}</Badge>
              )}
            </div>
            <div className="flex items-center space-x-4">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleRefresh}
                disabled={refreshing}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <span className="text-sm text-gray-600">
                {currentUser.name || currentUser.username || currentUser.email}
              </span>
              <Button variant="ghost" size="sm" onClick={handleLogout}>
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Dashboard Filters */}
        <Card className="mb-6">
          <CardHeader className="pb-4">
            <CardTitle>Dashboard Filters</CardTitle>
            <CardDescription>Filter data by date range and select base currency for conversion</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* From Date */}
              <div className="space-y-2">
                <Label>From Date</Label>
                <Input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="w-full"
                />
              </div>
              
              {/* To Date */}
              <div className="space-y-2">
                <Label>To Date</Label>
                <Input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="w-full"
                />
              </div>
              
              {/* Base Currency */}
              <div className="space-y-2">
                <Label>Base Currency</Label>
                <select
                  value={baseCurrency}
                  onChange={(e) => setBaseCurrency(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="USD">USD - US Dollar</option>
                  <option value="EUR">EUR - Euro</option>
                  <option value="GBP">GBP - British Pound</option>
                  <option value="CAD">CAD - Canadian Dollar</option>
                  <option value="AUD">AUD - Australian Dollar</option>
                  <option value="JPY">JPY - Japanese Yen</option>
                  <option value="CNY">CNY - Chinese Yuan</option>
                  <option value="INR">INR - Indian Rupee</option>
                </select>
              </div>
            </div>
            
            {/* Apply Button */}
            <div className="mt-4 flex justify-between items-center">
              <Button onClick={fetchAllData} disabled={refreshing}>
                <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Apply Filters
              </Button>
              
              {exchangeRates && (
                <p className="text-xs text-gray-500">
                  Exchange rates updated: {exchangeRates.time_last_update}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Currency Breakdown Section */}
    {currencyBreakdown.length > 0 && (
    <Card className="mb-6">
        <CardHeader className="pb-4">
        <CardTitle>Revenue by Currency</CardTitle>
        <CardDescription>
            {fromDate && toDate 
            ? `Revenue from ${fromDate} to ${toDate}` 
            : 'Revenue breakdown for today'}
        </CardDescription>
        </CardHeader>
        <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {currencyBreakdown.map(item => {
            const converted = exchangeRates ? convertCurrency(
                item.total_revenue,
                item.currency,
                baseCurrency,
                exchangeRates
            ) : 0;
            
            return (
                <div key={item.currency} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                    <Badge variant="outline">{item.currency}</Badge>
                    <span className="text-xs text-gray-500">
                    {item.campaign_count} campaign{item.campaign_count !== 1 ? 's' : ''}
                    </span>
                </div>
                <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(item.total_revenue, item.currency)}
                </p>
                {item.currency !== baseCurrency && exchangeRates && (
                    <p className="text-sm text-gray-500 mt-1">
                    ≈ {formatCurrency(converted, baseCurrency)}
                    </p>
                )}
                </div>
            );
            })}
            
            {/* Grand Total - only show if multiple currencies */}
            {currencyBreakdown.length > 1 && (
            <div className="p-4 bg-blue-50 rounded-lg border-2 border-blue-200">
                <div className="flex items-center justify-between mb-2">
                <Badge variant="default">Total ({baseCurrency})</Badge>
                </div>
                <p className="text-2xl font-bold text-blue-600">
                {formatCurrency(
                    currencyBreakdown.reduce((total, item) => {
                    if (!exchangeRates) return total;
                    const converted = convertCurrency(item.total_revenue, item.currency, baseCurrency, exchangeRates);
                    return total + converted;
                    }, 0),
                    baseCurrency
                )}
                </p>
                <p className="text-xs text-gray-600 mt-1">
                All currencies converted
                </p>
            </div>
            )}
        </div>
        </CardContent>
    </Card>
    )}

        {/* Performance Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <PerformanceCard
            title="Total Revenue"
            value={(() => {
                // Get the actual revenue value
                const rawRevenue = dashboardStats?.revenue_today || 0;
                
                // If we have currency breakdown data, calculate the converted total
                if (currencyBreakdown.length > 0 && exchangeRates) {
                const convertedTotal = currencyBreakdown.reduce((total, item) => {
                    const converted = convertCurrency(item.total_revenue, item.currency, baseCurrency, exchangeRates);
                    return total + converted;
                }, 0);
                return formatCurrency(convertedTotal, baseCurrency);
                }
                
                // Fallback: assume it's in primary currency and display as-is
                return formatCurrency(rawRevenue, baseCurrency);
            })()}
            subtitle={fromDate && toDate ? `${fromDate} to ${toDate}` : "Today"}
            change={`${formatCurrency(dashboardStats?.revenue_this_month || 0, primaryCurrency)} this month`}
            icon={<span className="text-xl font-bold">{getCurrencySymbol(baseCurrency)}</span>}
            trend={clickTrend}
            color="green"
            />
            
          <PerformanceCard
            title="Click Rate"
            value={`${dashboardStats?.click_rate_today || 0}%`}
            subtitle="Today's CTR"
            change={`${dashboardStats?.valid_clicks_today || 0} valid clicks`}
            icon={<Target className="h-5 w-5" />}
            trend={dashboardStats?.click_rate_today > 50 ? 'up' : 'down'}
            color="blue"
          />
          <PerformanceCard
            title="Traffic Quality"
            value={`${qualityScore.toFixed(1)}%`}
            subtitle="Last 7 days"
            change={`${trafficQuality?.invalid_clicks || 0} invalid clicks`}
            icon={<Shield className="h-5 w-5" />}
            trend={qualityScore > 70 ? 'up' : 'down'}
            color="purple"
          />
          <PerformanceCard
            title={isPublisher ? "Your Campaigns" : "Active Campaigns"}
            value={dashboardStats?.active_campaigns || 0}
            subtitle={`of ${dashboardStats?.total_campaigns || 0} total`}
            change={`${campaigns.filter(c => c.status === 'active').length} running`}
            icon={<Zap className="h-5 w-5" />}
            trend="stable"
            color="orange"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {bestPerformingCampaign && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-600">Top Campaign Today</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold">{bestPerformingCampaign.name}</p>
                    <p className="text-2xl font-bold text-green-600 mt-1">
                      {formatCurrency(bestPerformingCampaign.today_revenue || 0, bestPerformingCampaign.currency || 'USD')}
                    </p>
                    <p className="text-sm text-gray-500">
                      {bestPerformingCampaign.today_clicks || 0} clicks
                    </p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-green-500" />
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Invalid Click Types (7d)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {trafficQuality?.invalid_breakdown && (
                  <>
                    <TrafficBar label="Foreign" value={trafficQuality.invalid_breakdown.foreign} max={trafficQuality.invalid_clicks} color="orange" />
                    <TrafficBar label="Bot" value={trafficQuality.invalid_breakdown.bot} max={trafficQuality.invalid_clicks} color="red" />
                    <TrafficBar label="Expired" value={trafficQuality.invalid_breakdown.expired} max={trafficQuality.invalid_clicks} color="yellow" />
                    <TrafficBar label="Duplicate" value={trafficQuality.invalid_breakdown.duplicate} max={trafficQuality.invalid_clicks} color="purple" />
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Revenue Trend (7d)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-24">
                <MiniChart data={revenueStats?.revenue_by_day || []} />
              </div>
              <div className="flex justify-between items-center mt-2">
                <span className="text-sm text-gray-500">Total</span>
                <span className="text-lg font-bold">
                  {formatCurrency(revenueStats?.total_revenue || 0, primaryCurrency)}
                </span>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <Card>
            <CardHeader>
              <CardTitle>Click Performance</CardTitle>
              <CardDescription>Today's detailed metrics</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm font-medium">Overall Click Rate</span>
                    <span className="text-sm font-bold">{dashboardStats?.click_rate_today || 0}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div 
                      className={`h-3 rounded-full transition-all ${
                        (dashboardStats?.click_rate_today || 0) >= 70 ? 'bg-green-500' :
                        (dashboardStats?.click_rate_today || 0) >= 40 ? 'bg-yellow-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${dashboardStats?.click_rate_today || 0}%` }}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4 pt-2">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-900">
                      {dashboardStats?.total_clicks_today || 0}
                    </div>
                    <div className="text-xs text-gray-500">Total Clicks</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">
                      {dashboardStats?.valid_clicks_today || 0}
                    </div>
                    <div className="text-xs text-gray-500">Valid Clicks</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-red-600">
                      {dashboardStats?.invalid_clicks_today || 0}
                    </div>
                    <div className="text-xs text-gray-500">Invalid Clicks</div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Monthly Performance</CardTitle>
              <CardDescription>Revenue and activity this month</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <div className="flex items-baseline justify-between">
                    <span className="text-3xl font-bold text-green-600">
                      {formatCurrency(dashboardStats?.revenue_this_month || 0, primaryCurrency)}
                    </span>
                    <Badge variant={clickTrend === 'up' ? 'default' : clickTrend === 'down' ? 'destructive' : 'secondary'}>
                      {clickTrend === 'up' ? <TrendingUp className="h-3 w-3 mr-1" /> : 
                       clickTrend === 'down' ? <TrendingDown className="h-3 w-3 mr-1" /> : null}
                      {clickTrend}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">Total Revenue</p>
                </div>

                <div className="grid grid-cols-2 gap-4 pt-2 border-t">
                  <div>
                    <p className="text-sm text-gray-500">Avg Daily Revenue</p>
                    <p className="text-lg font-semibold">
                      {formatCurrency(revenueStats?.average_daily_revenue || 0, primaryCurrency)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Best Day</p>
                    <p className="text-lg font-semibold">
                      {formatCurrency(revenueStats?.best_day?.revenue || 0, primaryCurrency)}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle>
                  {isPublisher ? 'Assigned Campaigns' : 'Campaigns'}
                </CardTitle>
                <CardDescription>
                  {isPublisher 
                    ? 'Campaigns assigned to you by companies. Click to view detailed statistics.'
                    : 'Click on any campaign to view detailed analytics'}
                </CardDescription>
              </div>
              <div className="flex items-center space-x-2">
                <Badge variant="outline">
                  {campaigns.length} Total
                </Badge>
                <Badge variant="outline" className="text-green-600">
                  {campaigns.filter(c => c.status === 'active').length} Active
                </Badge>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            
            <div className="space-y-3">
              {campaigns.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  {isPublisher 
                    ? 'No campaigns assigned to you yet'
                    : 'No campaigns found'}
                </div>
              ) : (
                campaigns.map((campaign) => {
                    const performance = campaign.today_revenue > 0 ? 'good' : 
                                        campaign.today_clicks > 0 ? 'moderate' : 'low';
                    const currency = campaign.currency || 'USD';
                    const convStats = campaign.conversion_stats;
                    const hasConversions = convStats && convStats.total_conversions > 0;
                    
                    return (
                        <div 
                        key={campaign.id} 
                        className="border rounded-lg p-4 hover:shadow-md transition-all cursor-pointer group"
                        onClick={() => setSelectedCampaign(campaign)}
                        >
                        <div className="flex justify-between items-start">
                            <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                                <h3 className="font-semibold text-lg">{campaign.name}</h3>
                                <Badge variant={campaign.status === 'active' ? 'default' : 'secondary'}>
                                {campaign.status}
                                </Badge>
                                <Badge variant="outline" className="flex items-center gap-1">
                                <Globe className="h-3 w-3" />
                                {campaign.country_code}
                                </Badge>
                                <Badge variant="outline">
                                {currency}
                                </Badge>
                                <Badge 
                                variant="outline"
                                className={
                                    performance === 'good' ? 'text-green-600 border-green-300' :
                                    performance === 'moderate' ? 'text-yellow-600 border-yellow-300' :
                                    'text-gray-400 border-gray-300'
                                }
                                >
                                {performance === 'good' ? 'High Performance' :
                                performance === 'moderate' ? 'Active' : 'Low Activity'}
                                </Badge>
                                
                                {/* NEW: Conversion Badge */}
                                {hasConversions && (
                                <Badge variant="outline" className="text-blue-600 border-blue-300 flex items-center gap-1">
                                    <Target className="h-3 w-3" />
                                    {convStats.conversion_rate}% CR
                                </Badge>
                                )}
                            </div>
                            
                            <div className="grid grid-cols-2 md:grid-cols-6 gap-4 text-sm mt-3">
                                <div>
                                <span className="text-gray-500">
                                    {isPublisher ? 'Client' : 'Publisher'}
                                </span>
                                <p className="font-medium">
                                    {isPublisher 
                                    ? (campaign.client_name || 'Unknown Client')
                                    : (campaign.publisher_name || 'Not assigned')}
                                </p>
                                </div>
                                <div>
                                <span className="text-gray-500">CPC</span>
                                <p className="font-medium">
                                    {formatCurrency(campaign.cpc || 0, currency)}
                                </p>
                                </div>
                                <div>
                                <span className="text-gray-500">Today's Clicks</span>
                                <p className="font-medium flex items-center">
                                    <MousePointer className="h-3 w-3 mr-1" />
                                    {campaign.today_clicks || 0}
                                </p>
                                </div>
                                <div>
                                <span className="text-gray-500">Today's Revenue</span>
                                <p className="font-medium text-green-600 flex items-center">
                                    {formatCurrency(campaign.today_revenue || 0, currency)}
                                </p>
                                </div>
                                
                                {/* NEW: Conversions Column */}
                                <div>
                                <span className="text-gray-500">Conversions</span>
                                <p className="font-medium text-blue-600 flex items-center">
                                    {hasConversions ? (
                                    <>
                                        <Target className="h-3 w-3 mr-1" />
                                        {convStats.valid_conversions}
                                    </>
                                    ) : (
                                    <span className="text-gray-400">0</span>
                                    )}
                                </p>
                                </div>
                                
                                <div>
                                <span className="text-gray-500">Total Jobs</span>
                                <p className="font-medium">{campaign.total_urls || 0}</p>
                                </div>
                            </div>

                            {campaign.last_processed && (
                                <div className="flex items-center gap-1 text-xs text-gray-500 mt-2">
                                <Clock className="h-3 w-3" />
                                Last updated: {new Date(campaign.last_processed).toLocaleDateString()}
                                </div>
                            )}
                            </div>
                            
                            <div className="ml-4 opacity-0 group-hover:opacity-100 transition-opacity">
                            <ChevronRight className="h-5 w-5 text-gray-400" />
                            </div>
                        </div>
                        </div>
                    );
                    })
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

function PerformanceCard({ title, value, subtitle, change, icon, trend, color }) {
  const colorClasses = {
    green: 'bg-green-50 text-green-600 border-green-200',
    blue: 'bg-blue-50 text-blue-600 border-blue-200',
    purple: 'bg-purple-50 text-purple-600 border-purple-200',
    orange: 'bg-orange-50 text-orange-600 border-orange-200'
  };

  const trendIcon = trend === 'up' ? <TrendingUp className="h-4 w-4" /> :
                     trend === 'down' ? <TrendingDown className="h-4 w-4" /> :
                     <Activity className="h-4 w-4" />;

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
            {icon}
          </div>
          <div className={`flex items-center text-sm ${
            trend === 'up' ? 'text-green-600' :
            trend === 'down' ? 'text-red-600' :
            'text-gray-500'
          }`}>
            {trendIcon}
          </div>
        </div>
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold mt-1">{value}</p>
          <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
          <p className="text-xs text-gray-600 mt-2 font-medium">{change}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function TrafficBar({ label, value, max, color }) {
  const percentage = max > 0 ? (value / max * 100) : 0;
  const colorClasses = {
    orange: 'bg-orange-500',
    red: 'bg-red-500',
    yellow: 'bg-yellow-500',
    purple: 'bg-purple-500'
  };

  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-gray-600">{label}</span>
      <div className="flex items-center space-x-2">
        <div className="w-20 bg-gray-200 rounded-full h-2">
          <div 
            className={`h-2 rounded-full ${colorClasses[color]}`}
            style={{ width: `${percentage}%` }}
          />
        </div>
        <span className="text-xs font-medium w-8 text-right">{value}</span>
      </div>
    </div>
  );
}

function MiniChart({ data }) {
  if (!data || data.length === 0) return null;
  
  const maxRevenue = Math.max(...data.map(d => d.revenue || 0));
  const points = data.map((d, i) => {
    const x = (i / (data.length - 1)) * 100;
    const y = 100 - ((d.revenue / maxRevenue) * 100);
    return `${x},${y}`;
  }).join(' ');

  return (
    <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
      <polyline
        fill="none"
        stroke="#10b981"
        strokeWidth="2"
        points={points}
      />
    </svg>
  );
}